import { useGetQueueByAccountIdAndTypeQuery } from '@/services/queues';
import { Badge, BadgeVariant, Box, Button } from '@palmetto/palmetto-components';
import { useNavigate, useParams } from 'react-router-dom';

import {
  BillingDocumentTypes,
  InstallDocumentTypes,
  DomesticContentAttestmentType,
  QueueType,
  QueueStatus,
  RequirementFlagSettingsType,
  FlagTypeLabels,
  DCQualificationResponse,
  InstallPackageFileCategory,
} from 'types';

import FormSection from '../Forms/FormSection';
import { DocumentList } from '../MilestonePackagePartials/Documents/DocumentList';
import { FlagBox } from '../QueueFlags/FlagBox';

import { DomesticContentReview } from './DomesticContentReview';

type DomesticContentProps = {
  shouldDisplayDomesticContentReviewInformation?: boolean;
  attestmentType?: DomesticContentAttestmentType;
  domesticContentQualificationResult?: DCQualificationResponse;
  showCheckboxes: boolean;
  checkboxCategories: InstallPackageFileCategory[];
  isPackageEditable?: boolean;
};

const attestmentTypeBadgeMap: {
  [key in DomesticContentAttestmentType]: {
    message: string;
    variant: BadgeVariant;
  };
} = {
  pvOnly: {
    message: 'Solar Only',
    variant: 'primary',
  },
  storageOnly: {
    message: 'Storage Only',
    variant: 'secondary',
  },
  pvAndStorage: {
    message: 'Solar + Storage',
    variant: 'tertiary',
  },
  optOut: {
    message: 'Opted Out',
    variant: 'default',
  },
} as const;

export const DomesticContent = ({
  shouldDisplayDomesticContentReviewInformation,
  attestmentType,
  domesticContentQualificationResult,
  showCheckboxes,
  checkboxCategories,
  isPackageEditable = true,
}: DomesticContentProps) => {
  const { id } = useParams<{ id: any }>();
  const navigate = useNavigate();

  const { data: domesticContentQueue, isLoading: isDomesticContentQueueLoading } =
    useGetQueueByAccountIdAndTypeQuery({
      accountId: id,
      type: QueueType.domesticContent,
    });

  const domesticContentAttestmentTypeBadge = attestmentType ? attestmentTypeBadgeMap[attestmentType] : undefined;

  const showDCReSubmitAttestation =
    !domesticContentQueue ||
    [QueueStatus.rejected, QueueStatus.inProgress, QueueStatus.reReview].includes(domesticContentQueue.status);

  return (
    <FormSection
      title="Domestic Content"
      description={
        <Box gap="md">
          <Box>
            This project must pass review for Domestic Content (DC) compliance in order to qualify for DC incentive
            pricing.
          </Box>
          {domesticContentAttestmentTypeBadge && (
            <Box display="flex" gap="xs" alignItems="flex-start">
              <Badge
                variant={domesticContentAttestmentTypeBadge?.variant || 'default'}
                message={domesticContentAttestmentTypeBadge?.message || ''}
              />
            </Box>
          )}
          {domesticContentQualificationResult && (
            <>
              {domesticContentQualificationResult.pvQualified && (
                <Box>
                  <span>
                    <strong>DC Value (PV):</strong>{' '}
                    {(domesticContentQualificationResult.pvTotalPercentage ?? 0).toFixed(2)}%
                  </span>
                </Box>
              )}
              {domesticContentQualificationResult.batteryQualified && (
                <Box>
                  <span>
                    <strong>DC Value (Storage):</strong>{' '}
                    {(domesticContentQualificationResult.batteryPercentage ?? 0).toFixed(2)}%
                  </span>
                </Box>
              )}
            </>
          )}
        </Box>
      }
      {...(showDCReSubmitAttestation && {
        headerChildren: (
          <>
            <Box>
              Need to change your Domestic Content review type? Click the button below to return to the attestation
              page
            </Box>
            <Button
              onClick={() => navigate(`/accounts/${id}/itc-system-design`)}
              size="sm"
              variant="secondary"
              isLoading={isDomesticContentQueueLoading}
              disabled={isDomesticContentQueueLoading}
            >
              Re-submit Attestation
            </Button>
          </>
        ),
      })}
    >
      {isDomesticContentQueueLoading && (
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          backgroundColor="white"
          opacity="0.7"
          zIndex="overlay"
        />
      )}
      <Box gap="xl">
        {shouldDisplayDomesticContentReviewInformation && (
          <>
            <DomesticContentReview attestmentType={attestmentType} />
            <DocumentList
              documentTypeFilter={[InstallDocumentTypes.domesticContentDocuments]}
              title="System Details and Qualification"
              showTypeLabelOnItems={false}
              baseCategory={InstallDocumentTypes.domesticContentDocuments}
              showCheckboxes={showCheckboxes}
              checkboxCategories={checkboxCategories}
              isPackageEditable={isPackageEditable}
            />
          </>
        )}
        <DocumentList
          documentTypeFilter={[
            InstallDocumentTypes.invoice,
            InstallDocumentTypes.billOfMaterials,
            BillingDocumentTypes.incomingDirectPayInvoice,
          ]}
          title="Invoice/BOM Document"
          showTypeLabelOnItems={true}
          baseCategory={InstallDocumentTypes.domesticContentDocuments}
          showCheckboxes={showCheckboxes}
          checkboxCategories={checkboxCategories}
          isPackageEditable={isPackageEditable}
        />
        <FlagBox
          dataKey={FlagTypeLabels.domesticContentPhotos}
          flagType={RequirementFlagSettingsType.domesticContentPhotos}
        />
      </Box>
    </FormSection>
  );
};
